import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { ModalActionsComponent, PasslySelect, PasslyCheckbox } from '@app/components';
import { getLoaderBtn, getModalSwitch } from '@app/store/actions';
import { usersService } from '@app/services';

import { useTypedTranslation } from '@app/hooks';
import { provisioningStates, provisioned, active, onPressEnterModal } from '@app/helpers';

export interface IUpdateUsersData {
    usersList: any[];
    usersListId: string[];
    updateManyCallback: Function;
}

export default function UpdateUsersDialog(props: IUpdateUsersData) {
    // -------------------------------------------
    // ----------------- DATA --------------------
    // -------------------------------------------
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const [provisioningSelected, setProvisioningSelected] = useState(active);
    const [require2FA, setRequire2FA] = useState(true);
    const [sendEmail, setSendEmail] = useState(false);
    const [processingSave, setProcessingSave] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [errorList, setErrorList] = useState<any[]>([]);

    // -------------------------------------------
    // ----------------- FUNCTIONS ---------------
    // -------------------------------------------
    const onUpdateSuccess = (response: any) => {
        setProcessingSave(false);
        dispatch(getLoaderBtn(false));
        props.updateManyCallback(true);

        if (response.data && response.data.NotLicensed && response.data.NotLicensed.length) {
            // Some users failed to update

            let notLicensedUsers: any[] = [];

            for (let i = 0; i < props.usersList.length; i++) {
                for (let j = 0; j < response.data.NotLicensed.length; j++) {
                    if (props.usersList[i].id === response.data.NotLicensed[j]) {
                        notLicensedUsers.push({ displayName: props.usersList[i].displayName, principalName: props.usersList[i].principalName });
                    }
                }
            }

            setErrorList(notLicensedUsers);
            setErrorMessage(response.data.Reason);
        } else {
            dispatch(getModalSwitch(false));
        }
    };

    const onUpdateError = (error: any) => {
        setProcessingSave(false);
    };

    const handleSubmitBtn = () => {
        if (errorMessage !== '') {
            // Error screen is showing, nothing actionable to do except close
            handleCancel();
            return;
        }
        dispatch(getLoaderBtn(true));

        if (provisioningSelected === provisioned && sendEmail) {
            usersService.onboardMany({ People: props.usersListId, MFA: require2FA });
        }

        usersService.updatePeopleState({ People: props.usersListId, State: provisioningSelected }, onUpdateSuccess, onUpdateError);
    };

    const handleCancel = () => {
        props.updateManyCallback(false);
        dispatch(getModalSwitch(false));
    };

    const changeIsSendEmail = (checked: boolean) => setSendEmail(checked);
    const changeIsRequire2FA = (checked: boolean) => setRequire2FA(checked);
    const onSelectChange = (e: any) => setProvisioningSelected(e.target.value);

    return (
        <>
            <div className='modal__form' tabIndex={0} onKeyPress={onPressEnterModal(handleSubmitBtn, processingSave)}>
                <div className='modal__medium--fixed'>
                    {errorMessage !== '' ? (
                        <>
                            <p>
                                <div className='modal__error'>{translation('FAILED_BULK_UPDATE_MESSAGE')}</div>
                            </p>
                            <ul className='modal__bulk-update-fail-list'>
                                {errorList.map((row) => (
                                    <li className='modal__bulk-update-fail-item'>
                                        <b>{row.displayName}</b> (<span>{row.principalName}</span>)
                                    </li>
                                ))}
                            </ul>
                            <p>
                                <div className='modal__error'>{errorMessage}</div>
                            </p>
                        </>
                    ) : (
                        <>
                            <div className='modal__body--row'>
                                <p>{translation('UPDATE_MANY_USERS_STATE_BODY')}</p>

                                <form method='post'>
                                    <PasslySelect
                                        value={provisioningSelected}
                                        options={provisioningStates}
                                        onChange={onSelectChange}
                                        id='select-input'
                                        label={'PROVISIONING_POLICY'}
                                        appendTo={document.body}
                                    />

                                    {provisioningSelected === 'Provisioned' && (
                                        <>
                                            <div className='modal__body--content_section'>
                                                <PasslyCheckbox
                                                    name='sendEmail'
                                                    checked={sendEmail}
                                                    onChange={changeIsSendEmail}
                                                    label={translation('NOTIFY_USER')}
                                                />
                                            </div>
                                            <div className='modal__body--content_section'>
                                                <PasslyCheckbox
                                                    name='require2FA'
                                                    checked={require2FA}
                                                    onChange={changeIsRequire2FA}
                                                    label={translation('ENFORCE_MFA')}
                                                />
                                            </div>
                                        </>
                                    )}
                                </form>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <ModalActionsComponent
                primaryBtn={translation('OK')}
                secondaryBtn={errorMessage === '' ? translation('CANCEL') : null}
                onClickPrimary={handleSubmitBtn}
                onClickSecondary={handleCancel}
                disabled={processingSave}
                id='updateUser'
            />
        </>
    );
}
